if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isProductCarousel = this.hasAttribute('data-product-media-carousel');
        this.animationTimers = [];
        this.visibleThumbnailCount = 8;

        if (this.isProductCarousel) this.initProductCarousel();

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.rebindProductCarouselThumbnails();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      rebindProductCarouselThumbnails() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button || button.dataset.seedGalleryBound === 'true') return;

          button.dataset.seedGalleryBound = 'true';

          if (this.isProductCarousel) {
            button.addEventListener('click', (event) => {
              event.preventDefault();
              this.animateToMedia(mediaToSwitch.dataset.target);
            });
          } else {
            button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          }
        });
        if (this.isProductCarousel) {
          this.elements.thumbnails.querySelectorAll('.slider-button').forEach((button) => {
            if (button.dataset.seedGalleryBound === 'true') return;

            button.dataset.seedGalleryBound = 'true';
            button.addEventListener('click', (event) => {
              event.preventDefault();
              this.scrollThumbnailsBy(button.name === 'next' ? 1 : -1);
            });
          });
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails || !event.detail.currentElement) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        mediaId = this.normalizeMediaId(mediaId);
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        if (this.isProductCarousel && options.direction) this.setTransitionDirection(options.direction);
        if (this.isProductCarousel) this.setCurrentBatchByMediaId(activeMedia.dataset.mediaId);

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail?.parentElement?.firstChild !== activeThumbnail) {
              activeThumbnail?.parentElement?.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      initProductCarousel() {
        if (!this.elements.viewer) return;

        this.navigationCue = document.createElement('span');
        this.navigationCue.className = 'seed-product-gallery-cue';
        this.navigationCue.setAttribute('aria-hidden', 'true');
        this.navigationCue.innerHTML = '<span></span>';
        this.elements.viewer.append(this.navigationCue);

        this.elements.viewer.addEventListener('mousemove', (event) => this.updateProductCarouselCue(event));
        this.elements.viewer.addEventListener('mouseleave', () => this.clearProductCarouselCue());
        this.elements.viewer.addEventListener('click', (event) => this.onProductCarouselClick(event), true);

        this.zoomButtons = Array.from(this.querySelectorAll('[data-seed-gallery-zoom]'));
        this.zoomButtons.forEach((button) => {
          if (button.dataset.seedGalleryZoomBound === 'true') return;

          button.dataset.seedGalleryZoomBound = 'true';
          button.addEventListener('click', (event) => this.openActiveMediaModal(event));
        });

        this.productInfo = this.closest('product-info');
        this.rebuildColorBatches();
        this.activateSelectedColorBatch();
        this.productInfo?.addEventListener('change', (event) => this.onProductVariantOptionChange(event));
      }

      getMediaItems() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
      }

      normalizeMediaId(mediaId) {
        if (!mediaId) return mediaId;

        const requestedMediaId = String(mediaId);
        const mediaItems = this.getMediaItems();
        if (mediaItems.some((item) => item.dataset.mediaId === requestedMediaId)) return requestedMediaId;

        return (
          mediaItems.find((item) => item.dataset.mediaId === requestedMediaId || item.dataset.mediaId.endsWith(`-${requestedMediaId}`))
            ?.dataset.mediaId || requestedMediaId
        );
      }

      getActiveIndex() {
        return this.getMediaItems().findIndex((item) => item.classList.contains('is-active'));
      }

      getIndexByMediaId(mediaId) {
        mediaId = this.normalizeMediaId(mediaId);
        return this.getMediaItems().findIndex((item) => item.dataset.mediaId === mediaId);
      }

      getThumbnailItems() {
        return this.getAllThumbnailItems().filter((item) => !item.hidden);
      }

      getAllThumbnailItems() {
        if (!this.elements.thumbnails) return [];
        return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      }

      getThumbnailStep() {
        const thumbnails = this.getThumbnailItems();
        if (thumbnails.length < 2) return this.elements.thumbnails?.slider?.clientHeight || 0;
        return thumbnails[1].offsetTop - thumbnails[0].offsetTop;
      }

      rebuildColorBatches() {
        this.colorBatches = [];
        this.colorBatchStartsByValue = new Map();
        this.colorBatchStartsByName = new Map();
        if (!this.isProductCarousel || !this.productInfo) return;

        const mediaItems = this.getMediaItems();
        const colorLabels = Array.from(this.productInfo.querySelectorAll('[data-seed-variant-media-id]'));
        const colorEntries = colorLabels.map((label) => {
          const colorName = this.normalizeColorToken(label.dataset.seedVariantColorName || label.textContent);
          const colorValue = label.dataset.seedVariantColorValue || colorName;
          const nameIndexes = colorName ? this.getColorIndexes(mediaItems, colorName) : [];

          return {
            colorName,
            colorValue,
            nameIndexes,
            nameStart: nameIndexes[0] ?? -1,
            variantStart: this.getIndexByMediaId(label.dataset.seedVariantMediaId),
          };
        });
        const canUseColorIndexes =
          colorEntries.length > 0 &&
          colorEntries.every((entry) => entry.nameIndexes.length > 0) &&
          colorEntries.some((entry) => entry.nameIndexes.length > 1);

        const variantMediaStarts = Array.from(new Set(colorEntries
          .map((entry) => entry.variantStart)
          .filter((index) => index >= 0))).sort((a, b) => a - b);

        if (canUseColorIndexes) {
          this.colorBatches = colorEntries.map((entry) => ({
            start: entry.nameIndexes[0],
            end: entry.nameIndexes[entry.nameIndexes.length - 1] + 1,
            indexes: entry.nameIndexes,
          }));

          colorEntries.forEach((entry, index) => {
            const start = this.colorBatches[index]?.start;
            if (start < 0) return;

            if (entry.colorValue) this.colorBatchStartsByValue.set(entry.colorValue, start);
            if (entry.colorName) this.colorBatchStartsByName.set(entry.colorName, start);
          });
        } else {
          this.colorBatches = variantMediaStarts.map((start, index) => ({
            start,
            end: variantMediaStarts[index + 1] ?? mediaItems.length,
            indexes: this.getRangeIndexes(start, variantMediaStarts[index + 1] ?? mediaItems.length),
          }));
        }

        colorEntries.forEach((entry) => {
          const start = canUseColorIndexes ? entry.nameStart : entry.variantStart;
          if (start < 0) return;

          if (entry.colorValue) this.colorBatchStartsByValue.set(entry.colorValue, start);
          if (entry.colorName) this.colorBatchStartsByName.set(entry.colorName, start);
        });

        const activeMedia = mediaItems[this.getActiveIndex()];
        if (activeMedia) this.setCurrentBatchByMediaId(activeMedia.dataset.mediaId);
      }

      getBatchForMediaId(mediaId) {
        if (typeof this.colorBatches === 'undefined') this.rebuildColorBatches();

        const mediaIndex = this.getIndexByMediaId(mediaId);
        if (mediaIndex < 0 || !this.colorBatches?.length) return null;

        return this.colorBatches.find((batch) => batch.indexes.includes(mediaIndex)) || null;
      }

      setCurrentBatchByMediaId(mediaId) {
        const batch = this.getBatchForMediaId(mediaId);
        if (batch) {
          this.currentBatch = batch;
          this.applyCurrentBatchVisibility();
        }
        return batch;
      }

      applyCurrentBatchVisibility() {
        if (!this.isProductCarousel || !this.currentBatch) return;

        const mediaItems = this.getMediaItems();
        mediaItems.forEach((item, index) => {
          item.hidden = !this.currentBatch.indexes.includes(index);
        });

        this.getAllThumbnailItems().forEach((thumbnail) => {
          const mediaIndex = this.getIndexByMediaId(thumbnail.dataset.target);
          thumbnail.hidden = !this.currentBatch.indexes.includes(mediaIndex);
        });

        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      getCurrentBatchMediaItems() {
        const mediaItems = this.getMediaItems();
        const activeMedia = mediaItems[this.getActiveIndex()];
        if (!activeMedia) return mediaItems;

        const batch = this.currentBatch || this.getBatchForMediaId(activeMedia.dataset.mediaId);
        if (!batch || batch.indexes.length < 2) return mediaItems;

        return batch.indexes.map((index) => mediaItems[index]).filter(Boolean);
      }

      normalizeColorToken(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/&amp;/g, 'and')
          .replace(/&/g, 'and')
          .replace(/[^a-z0-9\u4e00-\u9fff]+/g, '');
      }

      getRangeIndexes(start, end) {
        return Array.from({ length: Math.max(end - start, 0) }, (_, index) => start + index);
      }

      getColorIndexes(mediaItems, colorName) {
        const sourceIndexes = this.getColorIndexesByDataset(mediaItems, colorName, 'seedMediaSrc');
        if (sourceIndexes.length > 0) return sourceIndexes;

        return this.getColorIndexesByDataset(mediaItems, colorName, 'seedMediaAlt');
      }

      getColorIndexesByDataset(mediaItems, colorName, datasetKey) {
        const indexes = mediaItems
          .map((item, index) => {
            const mediaText = this.normalizeColorToken(item.dataset[datasetKey] || '');
            return mediaText.includes(colorName) ? index : -1;
          })
          .filter((index) => index >= 0);

        return indexes.length > 0 && indexes.length < mediaItems.length ? indexes : [];
      }

      getMediaIdForColorLabel(label) {
        if (!label) return null;

        const mediaItems = this.getMediaItems();
        const colorValue = label.dataset.seedVariantColorValue;
        const colorName = this.normalizeColorToken(label.dataset.seedVariantColorName || label.textContent);
        const batchStart = this.colorBatchStartsByValue?.get(colorValue) ?? this.colorBatchStartsByName?.get(colorName);

        if (batchStart >= 0 && mediaItems[batchStart]) return mediaItems[batchStart].dataset.mediaId;

        return label.dataset.seedVariantMediaId;
      }

      activateSelectedColorBatch() {
        if (!this.isProductCarousel || !this.productInfo) return false;

        const selectedLabel = this.productInfo.querySelector('input[type="radio"]:checked + label[data-seed-variant-media-id]');
        const mediaId = this.getMediaIdForColorLabel(selectedLabel);
        if (!mediaId) return false;

        this.setActiveMedia(mediaId, false);
        return true;
      }

      animateToMedia(mediaId) {
        mediaId = this.normalizeMediaId(mediaId);
        if (!this.isProductCarousel) {
          this.setActiveMedia(mediaId, false);
          return;
        }

        this.clearAnimationTimers();
        const mediaItems = this.getMediaItems();
        const currentIndex = this.getActiveIndex();
        const targetIndex = this.getIndexByMediaId(mediaId);
        if (currentIndex < 0 || targetIndex < 0 || currentIndex === targetIndex) return;

        const currentBatch = this.getBatchForMediaId(mediaItems[currentIndex]?.dataset.mediaId);
        const targetBatch = this.getBatchForMediaId(mediaId);
        const isSameBatch = !currentBatch || !targetBatch || currentBatch === targetBatch;

        if (!isSameBatch) {
          const direction = targetIndex > currentIndex ? 'next' : 'prev';
          this.setActiveMedia(mediaId, false, { direction });
          return;
        }

        const pathItems =
          currentBatch && targetBatch
            ? currentBatch.indexes.map((index) => mediaItems[index]).filter(Boolean)
            : mediaItems;
        const currentPathIndex = pathItems.findIndex((item) => item.dataset.mediaId === mediaItems[currentIndex]?.dataset.mediaId);
        const targetPathIndex = pathItems.findIndex((item) => item.dataset.mediaId === mediaId);
        if (currentPathIndex < 0 || targetPathIndex < 0) return;

        const direction = targetPathIndex > currentPathIndex ? 'next' : 'prev';
        const distance = Math.abs(targetPathIndex - currentPathIndex);

        if (distance <= 3) {
          const step = targetPathIndex > currentPathIndex ? 1 : -1;
          let delay = 0;

          for (
            let index = currentPathIndex + step;
            step > 0 ? index <= targetPathIndex : index >= targetPathIndex;
            index += step
          ) {
            const mediaItem = pathItems[index];
            if (!mediaItem) continue;

            this.animationTimers.push(
              window.setTimeout(() => {
                this.setActiveMedia(mediaItem.dataset.mediaId, false, { direction });
              }, delay)
            );
            delay += 90;
          }

          return;
        }

        this.setActiveMedia(mediaId, false, { direction });
      }

      goToRelativeMedia(step) {
        const mediaItems = this.isProductCarousel ? this.getCurrentBatchMediaItems() : this.getMediaItems();
        if (!mediaItems.length) return;

        const currentIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        if (currentIndex < 0) return;

        const targetIndex = (currentIndex + step + mediaItems.length) % mediaItems.length;
        this.animateToMedia(mediaItems[targetIndex].dataset.mediaId);
      }

      scrollThumbnailsBy(step) {
        if (!this.elements.thumbnails?.slider) return;

        if (!this.mql.matches) {
          const visibleThumbnails = this.getThumbnailItems();
          const activeThumbnail = this.elements.thumbnails.querySelector('button[aria-current]')?.closest('[data-target]');
          const activeIndex = Math.max(visibleThumbnails.indexOf(activeThumbnail), 0);
          const targetIndex = activeIndex + step;
          const targetThumbnail = visibleThumbnails[targetIndex];
          if (targetThumbnail) {
            this.elements.thumbnails.slider.scrollTo({ left: targetThumbnail.offsetLeft, behavior: 'smooth' });
          }
          return;
        }

        const thumbnailStep = this.getThumbnailStep();
        if (!thumbnailStep) return;

        const scrollDelta = thumbnailStep * this.visibleThumbnailCount * (step > 0 ? 1 : -1);
        this.elements.thumbnails.slider.scrollTo({
          top: this.elements.thumbnails.slider.scrollTop + scrollDelta,
          behavior: 'smooth',
        });
      }

      onProductVariantOptionChange(event) {
        const input = event.target;
        if (!input?.matches?.('input[type="radio"]')) return;

        const escapedInputId = window.CSS?.escape ? CSS.escape(input.id) : input.id.replace(/"/g, '\\"');
        const label = this.productInfo?.querySelector(`label[for="${escapedInputId}"][data-seed-variant-media-id]`);
        const mediaId = this.getMediaIdForColorLabel(label);
        if (!mediaId) return;

        this.rebuildColorBatches();
        window.requestAnimationFrame(() => this.animateToMedia(mediaId));
      }

      updateProductCarouselCue(event) {
        if (event.target.closest?.('[data-seed-gallery-zoom]')) {
          this.clearProductCarouselCue();
          return;
        }

        const activeMedia = event.target.closest?.('.product__media-item.is-active');
        if (!activeMedia || !this.elements.viewer.contains(activeMedia)) {
          this.clearProductCarouselCue();
          return;
        }

        const mediaCount = this.isProductCarousel ? this.getCurrentBatchMediaItems().length : this.getMediaItems().length;
        if (mediaCount < 2) {
          this.clearProductCarouselCue();
          return;
        }

        const rect = activeMedia.getBoundingClientRect();
        const direction = event.clientX >= rect.left + rect.width / 2 ? 'next' : 'prev';

        if (this.navigationCue) {
          const viewerRect = this.elements.viewer.getBoundingClientRect();
          this.navigationCue.style.setProperty('--seed-gallery-cue-x', `${event.clientX - viewerRect.left}px`);
          this.navigationCue.style.setProperty('--seed-gallery-cue-y', `${event.clientY - viewerRect.top}px`);
        }

        this.classList.toggle('is-hover-next', direction === 'next');
        this.classList.toggle('is-hover-prev', direction === 'prev');
      }

      clearProductCarouselCue() {
        this.classList.remove('is-hover-next', 'is-hover-prev');
      }

      onProductCarouselClick(event) {
        if (event.target.closest?.('[data-seed-gallery-zoom]')) return;

        const activeMedia = event.target.closest?.('.product__media-item.is-active');
        if (!activeMedia || !this.elements.viewer.contains(activeMedia)) return;

        const isThumbnail = event.target.closest?.('[id^="GalleryThumbnails"]');
        const isPlayableMedia = activeMedia.classList.contains('product__media-item--full');
        if (isThumbnail || isPlayableMedia) return;

        event.preventDefault();
        event.stopPropagation();

        const rect = activeMedia.getBoundingClientRect();
        const direction = event.clientX >= rect.left + rect.width / 2 ? 1 : -1;
        this.goToRelativeMedia(direction);

      }

      openActiveMediaModal(event) {
        event.preventDefault();
        event.stopPropagation();

        const activeMedia = this.elements.viewer?.querySelector('.product__media-item.is-active');
        const activeMediaId = activeMedia?.dataset.mediaId?.split('-').pop();
        const activeToggle =
          activeMedia?.querySelector(`.product__media-toggle[data-media-id="${activeMediaId}"]`) ||
          activeMedia?.querySelector('.product__media-toggle');
        const modal = activeToggle?.closest('modal-opener')?.getAttribute('data-modal');
        const modalElement = modal ? document.querySelector(modal) : null;
        if (modalElement && activeToggle) modalElement.show(activeToggle);
      }

      setTransitionDirection(direction) {
        if (!this.isProductCarousel) return;

        this.classList.remove('is-moving-next', 'is-moving-prev');
        this.classList.add(direction === 'prev' ? 'is-moving-prev' : 'is-moving-next');

        window.clearTimeout(this.transitionTimer);
        this.transitionTimer = window.setTimeout(() => {
          this.classList.remove('is-moving-next', 'is-moving-prev');
        }, 220);
      }

      clearAnimationTimers() {
        this.animationTimers.forEach((timer) => window.clearTimeout(timer));
        this.animationTimers = [];
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.isProductCarousel && this.mql.matches) {
          const slider = this.elements.thumbnails.slider;
          if (!slider) return;
          const thumbnails = this.getThumbnailItems();
          const thumbnailIndex = thumbnails.indexOf(thumbnail);
          const firstVisibleIndex = Math.max(0, thumbnailIndex - Math.floor(this.visibleThumbnailCount / 2));
          const targetThumbnail = thumbnails[firstVisibleIndex];
          if (targetThumbnail) slider.scrollTo({ top: targetThumbnail.offsetTop - slider.offsetTop, behavior: 'smooth' });
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
